import Select from "react-select";
import styled from "styled-components";
import { useParams } from "react-router";
import {
  faEllipsis,
  faBullseye,
  faChartPie,
  faCircleInfo,
  faChartSimple
} from "@fortawesome/free-solid-svg-icons";
import { useRef, useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Loader from "../../loader/loader";
import {
  fetchCustomerWebsiteVersions,
  fetchCustomerActiveWebVersion,
  fetchCustomerWebsiteAnalytics
} from "../../../services/api-declaration";
import PieChart from "../../charts/pieChart";
import {
  DATE_FILTERS,
  PICKER_OPT_B,
  PICKER_LAYOUT
} from "../../date-range-pickers/picker-helper";
import { useTabRoute } from "../../../hooks/useTabRoute";
import { CustomCard, PageHeading } from "../../common-styling";
import { LS_TYPE_FILTERS, WEBSITE_CLICKS } from "../../strings";
import DateSelectorToolbar from "../../date-range-pickers/date-selector-toolbar";
import { useTranslation } from "react-i18next";

const AnalyticsGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(260px, 0.78fr) repeat(3, minmax(280px, 1fr));
  gap: 16px;
  margin-top: 8px;
  @media (max-width: 1399px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;
const AnalyticsCard = styled(CustomCard)`
  border-color: #dce5ef;
  border-radius: 8px;
  box-shadow: 0 10px 22px rgba(15, 37, 64, 0.05);
`;
const KeyMetricsCard = styled(AnalyticsCard)`
  min-height: 430px;
  @media (min-width: 1400px) {
    grid-row: span 2;
  }
`;
const ChartCard = styled(AnalyticsCard)`
  min-height: 260px;
`;
const CardHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
`;
const CardTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: #102047;
  font-size: 16px;
  font-weight: 700;
`;
const IconBadge = styled.div`
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ $color }) => $color};
  font-size: 24px;
`;
const RateBadge = styled.div`
  min-width: 56px;
  padding: 6px 12px;
  border-radius: 14px;
  color: ${({ $color }) => $color};
  background: ${({ $bgColor }) => $bgColor};
  font-size: 16px;
  font-weight: 700;
  text-align: center;
`;
const ChartContent = styled.div`
  display: grid;
  grid-template-columns: minmax(160px, 1fr) minmax(140px, 0.82fr);
  align-items: center;
  gap: 12px;
  @media (max-width: 575px) {
    grid-template-columns: 1fr;
  }
`;
const ChartBox = styled.div`
  min-width: 0;
`;
const LegendList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;
const LegendItem = styled.div`
  display: grid;
  grid-template-columns: 12px minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  color: #62718d;
  font-size: 14px;
`;
const LegendDot = styled.div`
  width: 11px;
  height: 11px;
  margin-top: 4px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
`;
const LegendLabel = styled.div`
  overflow-wrap: anywhere;
  white-space: normal;
`;
const LegendValue = styled.div`
  color: #102047;
  font-weight: 700;
`;
const MetricsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;
const MetricItem = styled.div`
  display: grid;
  grid-template-columns: 12px minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  min-height: 58px;
  padding: 14px 16px;
  border-radius: 8px;
  background: ${({ $color }) => `${$color}12`};
`;
const MetricLabel = styled.div`
  min-width: 0;
  color: #62718d;
  font-size: 14px;
  overflow-wrap: anywhere;
`;
const MetricValue = styled.div`
  color: #102047;
  font-size: ${({ $small }) => ($small ? "12px" : "18px")};
  font-weight: 700;
  text-align: right;
`;
const VersionSelectWrap = styled.div`
  min-width: 260px;
  margin-left: 8px;
  @media (max-width: 767px) {
    width: 100%;
    margin-left: 0;
    margin-top: 8px;
  }
`;

const analyticColors = {
  totalVisitCount: "#0078ff",
  uniqueVisitCount: "#21c997",
  totalCtaClicks: "#ad1eff",
  totalNonCtaClicks: "#fead02",
  totalLeads: "#dc3545"
};

const formatPercentage = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue.toFixed(2) : "0.00";
};

const CustomerWebsiteAnalytics = ({ isTabSelected }) => {
  const { t } = useTranslation();
  const { id: customerId } = useParams();
  const { tabRoute } = useTabRoute();
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingVersions, setIsLoadingVersions] = useState(true);
  const [analytics, setAnalytics] = useState({
    ctaClicksById: [],
    nonCtaClicksById: [],
    summary: {
      totalVisitCount: 0,
      uniqueVisitCount: 0,
      totalCtaClicks: 0,
      totalLeads: 0,
      assignedFunnelsCount: 0,
      totalNonCtaClicks: 0,
      ctaConversionRate: 0,
      leadConversionRate: 0
    }
  });
  const [versions, setVersions] = useState([]);
  const [currentActiveWebVersion, setCurrentActiveWebVersion] = useState("");
  const [selectedVersion, setSelectedVersion] = useState({});
  const dateSelectionRef = useRef({
    selectedDateFilter: DATE_FILTERS.ALL_TIME
  });
  const selectedVersionRef = useRef();
  const activeVersionReadyRef = useRef(false);
  const analyticsRequestIdRef = useRef(0);

  const fetchAnalytics = async ({
    selectedDateFilter = dateSelectionRef.current.selectedDateFilter,
    startDate = dateSelectionRef.current.startDate,
    endDate = dateSelectionRef.current.endDate,
    selectedVersionOverride
  }) => {
    if (!customerId) {
      setIsLoading(false);
      return;
    }
    // Retain changes made while the active version is still loading.
    dateSelectionRef.current = { selectedDateFilter, startDate, endDate };
    if (
      !activeVersionReadyRef.current &&
      !selectedVersionOverride &&
      !selectedVersionRef.current
    ) {
      return;
    }
    setIsLoading(true);
    const requestId = analyticsRequestIdRef.current + 1;
    analyticsRequestIdRef.current = requestId;
    try {
      const versionToFetch =
        selectedVersionOverride ||
        selectedVersionRef.current ||
        currentActiveWebVersion;
      const response = await fetchCustomerWebsiteAnalytics({
        customerId,
        selectedDateFilter,
        startDate,
        endDate,
        selectedVersion: versionToFetch,
        tabRoute
      });
      if (requestId === analyticsRequestIdRef.current) {
        setAnalytics(response.data.data);
      }
    } catch (error) {
      console.log("Error in fetching analytics: ", error);
    } finally {
      if (requestId === analyticsRequestIdRef.current) {
        setIsLoading(false);
      }
    }
  };
  const fetchWebsiteVersions = async (isCurrentLoad) => {
    try {
      if (!customerId) {
        return;
      }
      const response = await fetchCustomerWebsiteVersions({
        customerId,
        tabRoute
      });
      const versionRespOpts = (response.data?.data || []).map(
        ({ title, _id }) => ({
          name: title,
          id: _id
        })
      );
      const selectOptions =
        versionRespOpts.length === 1
          ? versionRespOpts
          : [{ name: t("website.allTime"), id: LS_TYPE_FILTERS.ALL }].concat(
              versionRespOpts
            );
      if (isCurrentLoad()) {
        setVersions(selectOptions);
      }
    } catch (error) {
      console.log("Error in fetching versions: ", error);
    } finally {
      if (isCurrentLoad()) {
        setIsLoadingVersions(false);
      }
    }
  };
  const fetchActiveWebsiteVersion = async () => {
    try {
      if (!customerId) {
        return;
      }
      const response = await fetchCustomerActiveWebVersion({
        customerId,
        tabRoute
      });
      return response.data.data.activeVersion;
    } catch (error) {
      console.log("Error in fetching active version: ", error);
      return "";
    }
  };
  const handleVersionChange = (option) => {
    setSelectedVersion(option);
    selectedVersionRef.current = option.id;
    fetchAnalytics({});
  };
  const generateCtaPieLabels = () =>
    analytics.ctaClicksById.map((cta) => {
      switch (cta.label) {
        case WEBSITE_CLICKS.FOOTER_CONTACT:
          return t("website.footerContact");
        case WEBSITE_CLICKS.FOOTER_CONTACT_OPTIONAL:
          return t("website.footerContactTwo");
        default:
          return cta.label;
      }
    });
  const generateNonCtaPieLabels = () =>
    analytics.nonCtaClicksById.map((nonCta) => {
      switch (nonCta.label) {
        case WEBSITE_CLICKS.NAVBAR_PHONE:
          return t("website.navbarPhone");
        case WEBSITE_CLICKS.NAVBAR_SECOND_PHONE:
          return t("website.navbarPhoneTwo");
        case WEBSITE_CLICKS.FOOTER_EMAIL:
          return t("website.footerEmail");
        case WEBSITE_CLICKS.FOOTER_PHONE:
          return t("website.footerPhone");
        default:
          return nonCta.label;
      }
    });

  const renderLegend = (items) => (
    <LegendList>
      {items.map((item) => (
        <LegendItem key={item.label}>
          <LegendDot $color={item.color} />
          <LegendLabel>{item.label}</LegendLabel>
          <LegendValue>{item.count}</LegendValue>
        </LegendItem>
      ))}
    </LegendList>
  );

  const renderChartCard = ({
    title,
    icon,
    iconColor,
    rate,
    rateColor,
    rateBgColor,
    showPie,
    pieSeries,
    pieLabels,
    pieColors,
    legendItems,
    showMenu = false
  }) => (
    <ChartCard className="p-3">
      <CardHeaderRow>
        <CardTitle>
          <IconBadge $color={iconColor}>
            <FontAwesomeIcon icon={icon} />
          </IconBadge>
          <span>{title}</span>
        </CardTitle>
        {rate !== undefined ? (
          <RateBadge $color={rateColor} $bgColor={rateBgColor}>
            {formatPercentage(rate)}%
          </RateBadge>
        ) : showMenu ? (
          <FontAwesomeIcon className="text-secondary" icon={faEllipsis} />
        ) : null}
      </CardHeaderRow>
      <ChartContent>
        <ChartBox>
          <PieChart
            chartWidth={220}
            chartHeight={205}
            series={pieSeries}
            labels={pieLabels}
            colors={pieColors}
            showLegend={false}
            percentageDecimals={2}
            tooltipFormatter={(_count) => _count}
          />
        </ChartBox>
        {showPie ? renderLegend(legendItems) : null}
      </ChartContent>
    </ChartCard>
  );

  useEffect(() => {
    if (!isTabSelected) return;

    let cancelled = false;
    const loadAnalyticsTabData = async () => {
      setIsLoading(true);
      setIsLoadingVersions(true);
      activeVersionReadyRef.current = false;
      analyticsRequestIdRef.current += 1;
      selectedVersionRef.current = undefined;
      setCurrentActiveWebVersion("");
      setSelectedVersion({});
      const activeVersion = await fetchActiveWebsiteVersion();
      if (cancelled) return;
      setCurrentActiveWebVersion(activeVersion);
      activeVersionReadyRef.current = true;
      await Promise.all([
        fetchWebsiteVersions(() => !cancelled),
        fetchAnalytics({ selectedVersionOverride: activeVersion })
      ]);
    };

    loadAnalyticsTabData();
    return () => {
      cancelled = true;
      activeVersionReadyRef.current = false;
      analyticsRequestIdRef.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTabSelected, customerId, tabRoute]);

  const keyMetrics = [
    {
      label: t("website.totalVisitCount"),
      count: analytics.summary.totalVisitCount,
      color: analyticColors.totalVisitCount
    },
    {
      label: t("website.uniqueVisitCount"),
      count: analytics.summary.uniqueVisitCount,
      color: analyticColors.uniqueVisitCount
    },
    {
      label: t("website.totalCTAClicks"),
      count: analytics.summary.totalCtaClicks,
      color: analyticColors.totalCtaClicks
    },
    {
      label: t("website.totalNonCTAClicks"),
      count: analytics.summary.totalNonCtaClicks,
      color: analyticColors.totalNonCtaClicks
    },
    {
      label: t("website.leads"),
      count: analytics.summary.assignedFunnelsCount
        ? analytics.summary.totalLeads
        : t("website.noFunnelsAssigned"),
      color: analyticColors.totalLeads,
      small: !analytics.summary.assignedFunnelsCount
    }
  ];
  const ctaConversionLegend = [
    {
      label: t("website.visitsWithoutCtaClick"),
      count: Math.max(
        analytics.summary.totalVisitCount - analytics.summary.totalCtaClicks,
        0
      ),
      color: analyticColors.totalVisitCount
    },
    {
      label: t("website.totalCTAClicks"),
      count: analytics.summary.totalCtaClicks,
      color: analyticColors.totalCtaClicks
    }
  ];
  const leadConversionLegend = [
    {
      label: t("website.visitsWithoutLead"),
      count: Math.max(
        analytics.summary.totalVisitCount - analytics.summary.totalLeads,
        0
      ),
      color: "#55a8ff"
    },
    {
      label: t("website.leads"),
      count: analytics.summary.totalLeads,
      color: analyticColors.totalLeads
    }
  ];
  const ctaPieLabels = generateCtaPieLabels();
  const ctaPieColors = [
    "#dd5e89",
    "#b29f94",
    "#feb018",
    "#fd4560",
    "#775dd0",
    "#2db5d3",
    "#d37b2d",
    "#b21f1f"
  ];
  const nonCtaPieLabels = generateNonCtaPieLabels();
  const nonCtaPieColors = [
    "#1bb5f3",
    "#f1c500",
    "#d2362d",
    "#a5d22d",
    "#fd0b60",
    "#455a64",
    "#616161",
    "#66BE0F"
  ];
  const ctaInfoLegend = ctaPieLabels.map((label, index) => ({
    label,
    count: analytics.ctaClicksById[index]?.count || 0,
    color: ctaPieColors[index % ctaPieColors.length]
  }));
  const nonCtaInfoLegend = nonCtaPieLabels.map((label, index) => ({
    label,
    count: analytics.nonCtaClicksById[index]?.count || 0,
    color: nonCtaPieColors[index % nonCtaPieColors.length]
  }));

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between flex-column flex-md-row py-2">
        <PageHeading className="mt-0">
          {t("website.analyticsHeading")}
        </PageHeading>
        <div className="d-flex align-items-center flex-column flex-md-row">
          <DateSelectorToolbar
            options={PICKER_OPT_B}
            fetchData={isTabSelected ? fetchAnalytics : () => {}}
            layout={PICKER_LAYOUT.DROPDOWN}
            defaultSelected={DATE_FILTERS.ALL_TIME}
          />
          <VersionSelectWrap>
            <Select
              isDisabled={isLoadingVersions}
              className="w-100"
              options={versions}
              value={
                Object.keys(selectedVersion).length
                  ? selectedVersion
                  : versions.find((item) => currentActiveWebVersion === item.id)
              }
              getOptionValue={(opt) => opt.id}
              getOptionLabel={(opt) => opt.name}
              onChange={(option) => {
                if (selectedVersion?.id !== option.id) {
                  handleVersionChange(option);
                }
              }}
            />
          </VersionSelectWrap>
        </div>
      </div>
      {isLoading ? (
        <CustomCard>
          <Loader calcHeight={500} />
        </CustomCard>
      ) : (
        <AnalyticsGrid>
          <KeyMetricsCard className="p-3">
            <CardHeaderRow>
              <CardTitle>
                <IconBadge $color={analyticColors.totalVisitCount}>
                  <FontAwesomeIcon icon={faChartSimple} />
                </IconBadge>
                <span>{t("website.keyMetrics")}</span>
              </CardTitle>
            </CardHeaderRow>
            <MetricsList>
              {keyMetrics.map((metric) => (
                <MetricItem key={metric.label} $color={metric.color}>
                  <LegendDot $color={metric.color} />
                  <MetricLabel>{metric.label}</MetricLabel>
                  <MetricValue $small={metric.small}>
                    {metric.count}
                  </MetricValue>
                </MetricItem>
              ))}
            </MetricsList>
          </KeyMetricsCard>
          {renderChartCard({
            title: t("website.ctaConversionRate"),
            icon: faChartPie,
            iconColor: analyticColors.totalVisitCount,
            rate: analytics.summary.ctaConversionRate,
            rateColor: analyticColors.totalCtaClicks,
            rateBgColor: "#ad1eff1f",
            showPie: analytics.summary.totalCtaClicks,
            pieSeries: [
              Math.max(
                analytics.summary.totalVisitCount -
                  analytics.summary.totalCtaClicks,
                0
              ),
              analytics.summary.totalCtaClicks
            ],
            pieLabels: [
              t("website.visitsWithoutCtaClick"),
              t("website.totalCTAClicks")
            ],
            pieColors: [
              analyticColors.totalVisitCount,
              analyticColors.totalCtaClicks
            ],
            legendItems: ctaConversionLegend
          })}
          {renderChartCard({
            title: t("website.leadConversionRate"),
            icon: faBullseye,
            iconColor: analyticColors.totalLeads,
            rate: analytics.summary.leadConversionRate,
            rateColor: analyticColors.totalLeads,
            rateBgColor: "#dc35451f",
            showPie: analytics.summary.totalLeads,
            pieSeries: [
              Math.max(
                analytics.summary.totalVisitCount -
                  analytics.summary.totalLeads,
                0
              ),
              analytics.summary.totalLeads
            ],
            pieLabels: [t("website.visitsWithoutLead"), t("website.leads")],
            pieColors: ["#55a8ff", analyticColors.totalLeads],
            legendItems: leadConversionLegend
          })}
          {renderChartCard({
            title: t("website.CTAInfo"),
            icon: faCircleInfo,
            iconColor: analyticColors.totalVisitCount,
            showPie: analytics.summary.totalCtaClicks,
            pieSeries: analytics.ctaClicksById.map((cta) => cta.count),
            pieLabels: ctaPieLabels,
            pieColors: ctaPieColors,
            legendItems: ctaInfoLegend,
            showMenu: true
          })}
          {renderChartCard({
            title: t("website.nonCTAInfo"),
            icon: faCircleInfo,
            iconColor: analyticColors.totalVisitCount,
            showPie: analytics.summary.totalNonCtaClicks,
            pieSeries: analytics.nonCtaClicksById.map((nonCta) => nonCta.count),
            pieLabels: nonCtaPieLabels,
            pieColors: nonCtaPieColors,
            legendItems: nonCtaInfoLegend,
            showMenu: true
          })}
        </AnalyticsGrid>
      )}
    </div>
  );
};
export default CustomerWebsiteAnalytics;
